/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { Formik } from 'formik';
import { memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { connect, ConnectedProps, useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router';
import * as Yup from 'yup';

import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
import PasswordIcon from '@mui/icons-material/Password';
import SwapIcon from '@mui/icons-material/SwapHorizRounded';
import { Button, CircularProgress, Collapse, Link, Stack, Tooltip, Typography } from '@mui/material';
import Box from '@mui/material/Box';

import { CredentialsError } from '../../components/CredentialsError/CredentialsError';
import { AvailableProvidersButtons } from '../../components/LoginActions/AvailableProvidersButtons';
import { useLoginActionsStyles } from '../../components/LoginActions/useLoginActionsStyles';
import { BasicRoutes } from '../../constants/basicRoutes';
import { Namespaces } from '../../i18n';
import { AuthenticationApiService } from '../../services/AuthenticationApiService';
import { RootState } from '../../store';
import { authorizeUserByToken } from '../../store/auth/actions';
import { ApiClientError } from '../../types/api/ApiClientError';
import { OidcStatus } from '../../types/authentication/SsoStatus';
import { LoginMode } from '../../types/common/LoginMode';
import { ALLOWED_ERROR_KEYS, CREDENTIAL_ERROR_KEYS, LoginErrorKey } from '../../types/login/LoginError';
import { fetchSetupProgress } from '../../utils/queryFns';
import { loginMode } from '../../utils/storageHelpers';
import { useDocumentTitle } from '../../utils/useDocumentTitle';
import { useObjectState } from '../../utils/useObjectState';
import { LoginForm } from './components/LoginForm/LoginForm';
import { SsoLoginForm } from './components/SsoLoginForm/SsoLoginForm';
import { createLoginPageStyles } from './LoginPage.styles';
import { LoginApiValidationErrors, LoginPageFormValues, ResetApiErrorForField } from './LoginPage.types';

const validationSchema = Yup.object({
  email: Yup.string().email('invalid_email').required('empty'),
  password: Yup.string()
    .min(8, 'invalid_password')
    .matches(/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).*$/, 'invalid_password')
    .required('empty'),
});

const mapState = (state: RootState) => ({
  auth: state.auth,
});

const connector = connect(mapState);
type PropsFromRedux = ConnectedProps<typeof connector>;
type LoginPageProps = PropsFromRedux & object;

type State = {
  isFetching: boolean;
  values: LoginPageFormValues;
  apiErrors: LoginApiValidationErrors;
  errorMessage?: string;
};
type LoginModeState = {
  mode: LoginMode;
  visibleMode: LoginMode | null;
};
type OidcState = {
  oidc: OidcStatus;
  isLoading: boolean;
};

const LoginPageComponent = memo<LoginPageProps>(props => {
  const { t } = useTranslation();
  const classes = createLoginPageStyles();
  const loginActionsClasses = useLoginActionsStyles();
  const dispatch = useDispatch();
  useDocumentTitle(t(`${Namespaces.Common}:page_titles.login`));
  const { auth } = props;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loginModeState, setLoginModeState] = useObjectState<LoginModeState>(() => {
    const savedMode = loginMode.get();
    return {
      mode: savedMode,
      visibleMode: savedMode,
    };
  });

  const [oidcState, setOidcState] = useObjectState<OidcState>({
    oidc: { status: 'none' },
    isLoading: true,
  });

  const prefillEmail = useMemo(() => searchParams.get('email'), [searchParams]);

  const [state, setState] = useObjectState<State>({
    isFetching: false,
    values: { email: prefillEmail ?? '', password: '' },
    apiErrors: {},
  });
  const { isFetching, values, errorMessage, apiErrors } = state;

  const setupProgressQuery = useQuery([fetchSetupProgress.key], fetchSetupProgress.fn);
  const setupProgress = useMemo(() => setupProgressQuery.data?.data.payload, [setupProgressQuery.data]);

  useEffect(() => {
    const fetchOidcStatus = async () => {
      const oidcStatus = await AuthenticationApiService.checkOidcStatus();
      setOidcState({ oidc: oidcStatus.data.payload, isLoading: false });
    };
    void fetchOidcStatus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (setupProgress?.steps.superuser === 'pending') {
      void navigate(BasicRoutes.SETUP);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupProgress]);

  const submit = async (data: LoginPageFormValues) => {
    const credentials = {
      email: data.email.trim(),
      password: data.password.trim(),
    };
    try {
      setState({ isFetching: true, errorMessage: undefined, apiErrors: {} });
      const token = await AuthenticationApiService.login(credentials);
      dispatch(authorizeUserByToken(token.data.payload.accessToken, new Date(token.data.payload.expires)));
      setState({ isFetching: false });
    } catch (e) {
      const error = e as ApiClientError;
      console.error(error.message);
      const backendMessage = error.response?.data.message;

      if (backendMessage && ['unprocessable_entity', 'invalid_credentials'].includes(backendMessage)) {
        setState({
          isFetching: false,
          errorMessage: backendMessage,
        });
      } else {
        const message =
          error.response?.status === 504
            ? 'system_not_available'
            : error.message === 'Network Error'
              ? 'network_error'
              : backendMessage || 'uncaught_error';
        setState({
          isFetching: false,
          errorMessage: message,
        });
      }
    }
  };

  const resetApiError: ResetApiErrorForField = name => {
    if (apiErrors[name]) {
      setState({ apiErrors: { ...apiErrors, [name]: undefined } });
    }
  };

  const getErrorTranslation = (errorMessage: string) => {
    const translationKey = `page.login.error.${errorMessage}`;
    const translation = t(`${Namespaces.Login}:${translationKey}`);

    if (translationKey === translation) {
      return t(`${Namespaces.Login}:page.login.error.invalid_client`);
    }

    return translation;
  };

  const shouldUseNewError = errorMessage ? ALLOWED_ERROR_KEYS.includes(errorMessage as LoginErrorKey) : false;
  const isCredentialError = errorMessage ? CREDENTIAL_ERROR_KEYS.includes(errorMessage as LoginErrorKey) : false;

  const error =
    errorMessage && !isCredentialError ? (
      shouldUseNewError ? (
        <CredentialsError show={!!errorMessage} errorKey={errorMessage as LoginErrorKey} userEmail={values.email} />
      ) : (
        <Tooltip title={getErrorTranslation(errorMessage)}>
          <p data-cy="login-form-error-message" className={classes.errorLine}>
            {getErrorTranslation(errorMessage)}
          </p>
        </Tooltip>
      )
    ) : null;

  const handleModeChange = (nextMode: LoginMode) => {
    setLoginModeState({ visibleMode: null, mode: nextMode });
    loginMode.set(nextMode);
  };

  const handleModeExited = () => {
    setLoginModeState({ visibleMode: loginModeState.mode });
  };

  return (
    <>
      <Box className={classes.formTitle}>{t('login:page.login.title')}</Box>
      {oidcState.isLoading ? (
        <div style={{ width: '100%', alignItems: 'center' }}>
          <CircularProgress size={24} />
        </div>
      ) : (
        <>
          {oidcState.oidc.status === 'none' ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center' }}>
              <Formik<LoginPageFormValues>
                initialValues={values}
                validationSchema={validationSchema}
                onSubmit={submit}
                validateOnChange
                validateOnMount
              >
                {props => (
                  <LoginForm
                    {...props}
                    isFetching={isFetching || auth.isFetching}
                    apiErrors={apiErrors}
                    resetApiError={resetApiError}
                    credentialsError={isCredentialError ? (errorMessage as LoginErrorKey) : undefined}
                    resetCredentialsError={() => setState({ errorMessage: undefined })}
                  />
                )}
              </Formik>
              {error}
            </Box>
          ) : (
            <>
              <Collapse in={loginModeState.visibleMode === LoginMode.Select} onExited={handleModeExited}>
                <Box
                  sx={{
                    display: 'flex',
                    flexFlow: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    marginTop: '10px',
                  }}
                >
                  <Button
                    variant="contained"
                    startIcon={<KeyRoundedIcon />}
                    onClick={() => handleModeChange(LoginMode.SSO)}
                    className={classes.button}
                  >
                    <div className={classes.buttonRoot}>
                      <Typography className={classes.buttonMainText}>
                        {t('login:page.login.sso.button_title_main')}
                      </Typography>
                      <Typography className={classes.buttonHelperText}>
                        {t('login:page.login.sso.button_title_annotation')}
                      </Typography>
                    </div>
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<PasswordIcon />}
                    onClick={() => handleModeChange(LoginMode.Password)}
                    className={classes.button}
                  >
                    <div className={classes.buttonRoot}>
                      <Typography className={classes.buttonMainText}>
                        {t('login:page.login.password.button_title_main')}
                      </Typography>
                      <Typography className={classes.buttonHelperText}>
                        {t('login:page.login.password.button_title_annotation')}
                      </Typography>
                    </div>
                  </Button>
                </Box>
              </Collapse>
              <Collapse in={loginModeState.visibleMode === LoginMode.SSO} onExited={handleModeExited}>
                <>
                  <Typography className={classes.formSubtitle}>{t('login:page.login.sso.subtitle')}</Typography>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center' }}
                  >
                    <SsoLoginForm
                      swapToPasswordButton={
                        <Link
                          component="button"
                          onClick={() => handleModeChange(LoginMode.Password)}
                          className={loginActionsClasses.ssoLink}
                        >
                          <SwapIcon sx={{ fontSize: '18px' }} />
                          {t('login:page.login.sso.use_password')}
                        </Link>
                      }
                      handleLoginModeChange={handleModeChange}
                    />
                  </Box>
                </>
              </Collapse>
              <Collapse in={loginModeState.visibleMode === LoginMode.Password} onExited={handleModeExited}>
                <>
                  <Typography className={classes.formSubtitle}>{t('login:page.login.password.subtitle')}</Typography>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center' }}
                  >
                    <Formik<LoginPageFormValues>
                      initialValues={values}
                      validationSchema={validationSchema}
                      onSubmit={submit}
                      validateOnChange
                      validateOnMount
                    >
                      {props => (
                        <LoginForm
                          {...props}
                          isFetching={isFetching || auth.isFetching}
                          apiErrors={apiErrors}
                          resetApiError={resetApiError}
                          credentialsError={isCredentialError ? (errorMessage as LoginErrorKey) : undefined}
                          resetCredentialsError={() => setState({ errorMessage: undefined })}
                          swapToSsoButton={
                            <Link
                              component="button"
                              onClick={() => handleModeChange(LoginMode.SSO)}
                              className={loginActionsClasses.ssoLink}
                            >
                              <SwapIcon sx={{ fontSize: '18px' }} />
                              {t('login:page.login.password.use_sso')}
                            </Link>
                          }
                        />
                      )}
                    </Formik>
                    {error}
                  </Box>
                </>
              </Collapse>
            </>
          )}
          <Stack spacing={1} sx={{ marginTop: theme => theme.spacing(2) }}>
            <AvailableProvidersButtons />
          </Stack>
        </>
      )}
    </>
  );
});

export const LoginPage = connector(LoginPageComponent);
